import { IndexableDocument, ODataArrayResponse } from "hooks/queries/useDocuments";
import { useWorkspace } from "hooks/queries/useWorkspace";
import { useParams } from "react-router-dom";
import { axiosBeyonClient } from "utils/beyonAuth/beyonAxiosClient";
import { useEffect, useRef, useState } from "react";
import { EditForm } from "components/Form/EditForm";
import { useMutation } from "@tanstack/react-query";
import { useView, ViewConfig } from "hooks/queries/useView";
import { Tag } from "types/Tag";
import { buildTagDeltaPayload } from "./buildTagDeltaPayload";
import { useTags } from "hooks/queries/useTags";
import { WorkspaceConfig } from "types/WorkspaceConfig";
import { Button, Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Save } from "@mui/icons-material";

export type SingleFileEditProps = {
  documentId: string,
  onSuccess?: () => void,
  onCancel?: () => void,
}

export function SingleDocumentEdit({ documentId, onSuccess, onCancel }: SingleFileEditProps) {

  const { workspaceId, viewId } = useParams<{ workspaceId: string; viewId: string; }>();
  const workspace = useWorkspace(workspaceId);
  const workspaceConfig = workspace.data?.config;
  const connectorConfig = workspaceConfig?.connectorConfig;
  const tagConfig = workspaceConfig?.tagConfig;
  const viewConfig = useView(workspaceId, viewId)?.data;
  const editPropertiesConfig = viewConfig?.editFormConfig?.properties;

  const url = `${connectorConfig?.apiEndpoint}/${connectorConfig?.entity}`;

  const [documentData, setDocumentData] = useState<IndexableDocument>();

  const tags = useTags({ workspaceId, workspaceConfig, enabled: !!tagConfig });

  const originalTags = useRef<Tag[]>();

  useEffect(() => {
    const fetchExistingData = async () => {
      let queryUrl = `${url}/${documentId}`;

      if (tagConfig) {
        if (!tagConfig.documentTagCollectionProperty)
          throw new Error("documentCollectionProperty is missing in the workspace tagConfig");

        queryUrl += `?$expand=${tagConfig.documentTagCollectionProperty}`
      }

      const { data } = await axiosBeyonClient.get<IndexableDocument>(queryUrl);

      if (tagConfig) {
        if (!tagConfig.tagValueProperty)
          throw new Error("valueProperty is missing in the workspace tagConfig");

        if (!tagConfig.documentTagCollectionProperty)
          throw new Error("collectionProperty is missing in the workspace tagConfig");

        data.tags = mapTagValueProperty(tagConfig.tagValueProperty, data[tagConfig.documentTagCollectionProperty]);
        originalTags.current = data.tags;
      }

      setDocumentData(data);
    };

    fetchExistingData();
  }, [documentId, tagConfig, url]);

  const updateDocumentMutation = useMutation({
    mutationFn: async () => {

      const payload: Record<string, any> = getPatchPayload(workspaceConfig, viewConfig, documentId, documentData)

      if (tagConfig) {
        if (!tagConfig.documentTagCollectionProperty || !tagConfig.tagDocumentIdProperty || !tagConfig.tagValueProperty)
          throw new Error("TagConfiguration is invalid or imcomplete");

        payload[`${tagConfig.documentTagCollectionProperty}@delta`] = buildTagDeltaPayload(originalTags.current, documentData.tags, documentId, tagConfig.tagDocumentIdProperty, tagConfig.tagValueProperty);
      }

      const response = await axiosBeyonClient.patch<ODataArrayResponse<IndexableDocument>>(`${url}/${documentId}`, payload);


      if (response.status >= 200 && response.status < 300) {
        onSuccess && onSuccess();
        return response.data;
      }
      else
        throw new Error(`Failed to update document: ${response.statusText}`);
    }
  });

  return (
    <Stack gap={1}>
      {updateDocumentMutation.isError &&
        <div>Error updating document</div>
      }
      <EditForm
        item={documentData}
        properties={workspaceConfig?.propertiesSchema.filter(x => editPropertiesConfig.includes(x.name))}
        enableTags={tagConfig && viewConfig?.editFormConfig?.enableTags}
        tagOptions={tags?.data}
        onChange={setDocumentData}
      />
      <Stack direction="row" gap={1} justifyContent="end">
        <Button
          color="primary"
          variant="outlined"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <LoadingButton
          color="primary"
          variant="contained"
          loadingPosition="end"
          endIcon={<Save />}
          loading={updateDocumentMutation.isPending}
          onClick={() => updateDocumentMutation.mutateAsync()}
        >
          Save
        </LoadingButton>
      </Stack>
    </Stack>
  );
}

function mapTagValueProperty(valueProperty: string, tags: Record<string, string>[]): Tag[] {
  return (tags || []).map(x => ({
    value: x[valueProperty]
  }))
}


function getPatchPayload(workspaceConfig: WorkspaceConfig, viewConfig: ViewConfig, documentId: string, documentData: Record<string, any>) {

  const payload: Record<string, any> = {
    [workspaceConfig.connectorConfig.idProperty]: documentId
  };

  const editableProperties = workspaceConfig.propertiesSchema.filter(x => !x.readOnly);

  viewConfig.editFormConfig.properties.forEach(viewProperty => {
    const workspaceProperty = editableProperties.find(x => x.name === viewProperty);
    if (!workspaceProperty)
      return;

    payload[viewProperty] = documentData[viewProperty];
  })
  return payload;
}